import React, { useEffect, useMemo, useState, useRef } from "react";
import { createPortal } from "react-dom";
import SinglePhotoCell from "./SinglePhotoCell";
import PhotoUploader from "./PhotoUploader";
import { Trash2, Plus, ChevronDown, ChevronRight, FolderPlus, Pencil, GripVertical, Camera } from "lucide-react";
import {
  DndContext,
  PointerSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  useDroppable,
  pointerWithin,
  DragOverlay,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

export const NOTE1_OPTIONS = [
  { value: "", label: "—" },
  { value: "tabella_portata_errata", label: "Tabella portata errata" },
  { value: "tabella_portata_mancante", label: "Tabella portata mancante" },
  { value: "deformazione", label: "Deformazione" },
  { value: "modifica_non_conforme", label: "Modifica non conforme" },
  { value: "torsione", label: "Torsione" },
  { value: "fissaggio_mancante", label: "Fissaggio mancante" },
  { value: "pezzo_mancante", label: "Pezzo mancante" },
  { value: "antisgancio_mancante", label: "Antisgancio mancante" },
  { value: "basi_non_conformi", label: "Basi non conformi" },
  { value: "carico_pericoloso", label: "Carico pericoloso" },
];

const DAMAGE_COLS = [
  { key: "montante_anteriore", short: "M.Ant", long: "Montante Ant." },
  { key: "montante_posteriore", short: "M.Post", long: "Montante Post." },
  { key: "diagonali", short: "Diag.", long: "Diagonali", countable: true },
  { key: "traversino_basso", short: "Trav.", long: "Traversino", countable: true },
  { key: "corrente", short: "Corr.", long: "Corrente", countable: true },
];

// Cells with `countable: true` accept a multi-selection of 1-10 numeric badges
// in addition to a colour, to indicate which/how many components of that
// severity are damaged.
const COUNTABLE_KEYS = new Set(DAMAGE_COLS.filter((c) => c.countable).map((c) => c.key));
const countKey = (key) => `${key}_count`;

/** Normalise a count value to an array of unique sorted ints (1-10). Accepts:
 *   • null/undefined → null (no count)
 *   • [] / "" → null
 *   • a single int (legacy single-pick) → wrapped as [int]
 *   • an array of ints (multi-pick) → de-duped + sorted */
function normaliseCounts(v) {
  if (v === null || v === undefined || v === "") return null;
  const arr = Array.isArray(v) ? v : [v];
  const cleaned = Array.from(new Set(
    arr
      .map((x) => parseInt(x, 10))
      .filter((n) => Number.isFinite(n) && n >= 1 && n <= 10)
  )).sort((a, b) => a - b);
  return cleaned.length ? cleaned : null;
}

const DEFAULT_AREA = "Generale";
const AREA_PLACEHOLDER_PREFIX = "area-end::";
const AREA_DRAG_PREFIX = "area::";

// ---- helpers --------------------------------------------------------------

const areaOf = (e) => (e?.area && String(e.area).trim()) || DEFAULT_AREA;

const newId = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `id_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`);

function ensureDndIds(entries) {
  return entries.map((e) => (e && e._dndId ? e : { ...e, _dndId: newId() }));
}

function groupByArea(entries) {
  const map = new Map();
  entries.forEach((e, idx) => {
    const a = areaOf(e);
    if (!map.has(a)) map.set(a, []);
    map.get(a).push({ entry: e, idx });
  });
  return Array.from(map.entries()).map(([name, items]) => ({ name, items }));
}

function blankRow(area) {
  return {
    _dndId: newId(),
    area: area || DEFAULT_AREA,
    riferimento_scaffalatura: "",
    posto: "",
    montante_anteriore: null,
    montante_posteriore: null,
    diagonali: null,
    diagonali_count: null,
    traversino_basso: null,
    traversino_basso_count: null,
    corrente: null,
    corrente_count: null,
    nota1: null,
    nota2: "",
    photo: null,
  };
}

function LevelPicker({ value, onChange, count = null, onCountChange = null, countable = false }) {
  // Picker state: which colour was clicked + where to place the portal popup
  const [picker, setPicker] = useState(null); // { color, left, top, width } or null
  const buttonsRef = useRef({});

  // `count` accepts both legacy single integer and the new array of integers
  // (multi-selection). We normalise to an array for internal use.
  const counts = normaliseCounts(count) || [];

  // Close the count popup on outside click / Esc / scroll
  useEffect(() => {
    if (!picker) return undefined;
    const onClick = (e) => {
      // Allow clicks inside the portal popup
      if (e.target.closest?.("[data-level-count-popup]")) return;
      setPicker(null);
    };
    const onKey = (e) => { if (e.key === "Escape") setPicker(null); };
    const onScroll = () => setPicker(null);
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [picker]);

  const colors = {
    verde: "#10b981",
    arancione: "#ffcc00",
    rosso: "#ff3b30",
  };
  const titleFor = (v) => ({ verde: "Nessun danno", arancione: "Attenzione", rosso: "Grave" }[v]);

  const openPickerNear = (v) => {
    const btn = buttonsRef.current[v];
    if (!btn) return;
    const r = btn.getBoundingClientRect();
    const POPUP_W = 200;  // wider than 5 columns of 24px buttons + padding
    const left = Math.max(8, Math.min(window.innerWidth - POPUP_W - 8, r.left + r.width / 2 - POPUP_W / 2));
    const top = r.bottom + 4;
    setPicker({ color: v, left, top, width: POPUP_W });
  };

  const clickColor = (v) => {
    if (value === v) {
      // Toggle off: clear both level and count
      onChange(null);
      setPicker(null);
      return;
    }
    onChange(v);
    if (countable && onCountChange) openPickerNear(v);
  };

  // Toggle a single number in / out of the count array. The popup stays open
  // so the user can pick several numbers in one go. When the resulting set
  // becomes empty (user toggled off the last number) we also clear the
  // selected colour and close the picker — that way "click colour → click
  // number → click same number" undoes the whole selection in one go.
  const toggleCount = (n) => {
    const next = counts.includes(n)
      ? counts.filter((x) => x !== n)
      : [...counts, n].sort((a, b) => a - b);
    if (next.length === 0) {
      onChange(null);
      setPicker(null);
    } else {
      onCountChange?.(next);
    }
  };

  const clearCounts = () => {
    // "Pulisci" wipes both the count AND the colour, then closes the popup —
    // it's a full-undo of the cell selection.
    onChange(null);
    setPicker(null);
  };

  const baseCls = "w-5 h-5 border-[1.5px] border-black transition-transform active:translate-y-0.5";
  const selectedCls = " ring-2 ring-black ring-offset-1";

  // Render up to 4 numbers comma-separated; if more, append "…".
  const renderBadge = () => {
    if (counts.length === 0) return null;
    const shown = counts.slice(0, 4).join(",");
    const txt = counts.length > 4 ? `${shown}…` : shown;
    // Auto-shrink font based on string length so it fits inside the 24px cell.
    const fontPx = txt.length <= 1 ? 10 : txt.length <= 3 ? 9 : 7;
    return (
      <span style={{ fontSize: `${fontPx}px`, lineHeight: 1 }} className="font-bold whitespace-nowrap">
        {txt}
      </span>
    );
  };

  return (
    <div className="inline-flex gap-1 items-center justify-center">
      {["verde", "arancione", "rosso"].map((v) => {
        const isOn = value === v;
        const showCount = countable && isOn && counts.length > 0;
        return (
          <button
            key={v}
            ref={(el) => { buttonsRef.current[v] = el; }}
            type="button"
            onClick={() => clickColor(v)}
            className={`${baseCls} ${isOn ? selectedCls : "hover:opacity-80"} relative flex items-center justify-center`}
            style={{
              backgroundColor: colors[v],
              color: v === "arancione" ? "#000" : "#fff",
            }}
            aria-label={`${titleFor(v)}${showCount ? ` · ${counts.join(",")}` : ""}`}
            title={countable ? `${titleFor(v)} · clicca per scegliere uno o più numeri (1-10)` : titleFor(v)}
            data-testid={`level-${v}`}
          >
            {showCount && renderBadge()}
          </button>
        );
      })}

      {picker && typeof document !== "undefined" && createPortal(
        <div
          data-level-count-popup
          className="fixed z-[80] bg-white border-[1.5px] border-black rounded-md shadow-xl p-1.5"
          style={{ left: picker.left, top: picker.top, width: picker.width }}
          onMouseDown={(e) => e.stopPropagation()}
          data-testid="level-count-picker"
        >
          <div className="flex items-center justify-between gap-2 px-1 mb-1">
            <div className="text-[9px] uppercase tracking-wider font-bold text-neutral-500 whitespace-nowrap">
              Numeri (selez. multipla)
            </div>
            <button
              type="button"
              onClick={clearCounts}
              className="text-[9px] uppercase tracking-wider font-bold text-neutral-500 hover:text-[#ff3b30] underline"
              data-testid="level-count-clear"
            >
              Pulisci
            </button>
          </div>
          <div className="grid grid-cols-5 gap-1">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => {
              const on = counts.includes(n);
              return (
                <button
                  key={n}
                  type="button"
                  onClick={() => toggleCount(n)}
                  className={`h-7 text-[12px] font-bold border border-black rounded hover:bg-[#ff5a00] hover:text-white transition-colors ${
                    on ? "bg-black text-white" : "bg-white text-black"
                  }`}
                  data-testid={`level-count-${n}`}
                  aria-pressed={on}
                >
                  {n}
                </button>
              );
            })}
          </div>
          <div className="flex justify-end mt-2">
            <button
              type="button"
              onClick={() => setPicker(null)}
              className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 bg-black text-white rounded hover:bg-[#ff5a00]"
              data-testid="level-count-done"
            >
              Fatto
            </button>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}

// Custom hook: are we on desktop (md+ ≈ 768px)?
function useIsDesktop(breakpoint = 768) {
  const [isDesktop, setIsDesktop] = useState(() => {
    if (typeof window === "undefined") return true;
    return window.matchMedia(`(min-width: ${breakpoint}px)`).matches;
  });
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const mq = window.matchMedia(`(min-width: ${breakpoint}px)`);
    const cb = (e) => setIsDesktop(e.matches);
    setIsDesktop(mq.matches);
    if (mq.addEventListener) mq.addEventListener("change", cb);
    else mq.addListener(cb);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", cb);
      else mq.removeListener(cb);
    };
  }, [breakpoint]);
  return isDesktop;
}

// ---------------------------------------------------------------------------

// ─────────────────────────────────────────────────────────────────────────────
// Area-level multi-select metadata (typology, static/seismic, load tables).
// Each area on a scaffold verification can optionally be tagged with any
// combination of the following. When ALL groups for an area are empty the
// entire block is omitted from the PDF — matches the user's requirement:
// "Se non seleziono qualcosa, nel pdf non compare niente."
// ─────────────────────────────────────────────────────────────────────────────
export const AREA_META_GROUPS = [
  {
    key: "type",
    label: "Tipologia",
    options: [
      { key: "portapallet", label: "Portapallet" },
      { key: "picking", label: "Picking" },
      { key: "cantilever", label: "Cantilever" },
      { key: "drive_in", label: "Drive In" },
    ],
  },
  {
    key: "static_seismic",
    label: "Comportamento sismico",
    options: [
      { key: "statica", label: "Statica" },
      { key: "antisismica", label: "Antisismica" },
    ],
  },
  {
    key: "load_tables",
    label: "Tabelle di portata",
    options: [
      { key: "conformi", label: "Conformi" },
      { key: "non_conformi", label: "Non conformi" },
      { key: "assenti", label: "Assenti" },
    ],
  },
];

export const AREA_META_LOOKUP = Object.fromEntries(
  AREA_META_GROUPS.map((g) => [g.key, {
    label: g.label,
    options: Object.fromEntries(g.options.map((o) => [o.key, o.label])),
  }]),
);

export default function DamageEntriesTable({
  value = [],
  onChange,
  areaNotes = {},
  onAreaNotesChange,
  areaPhotos = {},
  onAreaPhotosChange,
  areaMeta = {},
  onAreaMetaChange,
  readOnly = false,
}) {
  const entries = value;

  useEffect(() => {
    if (!entries.length) return;
    const missing = entries.some((e) => !e._dndId);
    if (missing) onChange(ensureDndIds(entries));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entries.length]);

  const groups = useMemo(() => groupByArea(entries), [entries]);

  const [expandedArea, setExpandedArea] = useState(null);
  const [renamingArea, setRenamingArea] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [activeDragId, setActiveDragId] = useState(null);

  useEffect(() => {
    if (readOnly) return;
    if (groups.length === 0) { setExpandedArea(null); return; }
    if (!groups.some((g) => g.name === expandedArea)) setExpandedArea(groups[0].name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups.length, readOnly]);

  // ---- mutators ----------------------------------------------------------
  const updateRow = (idx, patch) => {
    onChange(entries.map((e, i) => (i === idx ? { ...e, ...patch } : e)));
  };
  const removeRow = (idx) => {
    onChange(entries.filter((_, i) => i !== idx));
  };
  /** Insert a blank row at absolute position `absIdx` (0..entries.length). The
   * new row inherits the same area as the surrounding rows. Used by the "+"
   * gaps between rows in DesktopTable / MobileCards. */
  const insertRow = (absIdx, areaName) => {
    const safeIdx = Math.max(0, Math.min(absIdx, entries.length));
    const next = [
      ...entries.slice(0, safeIdx),
      blankRow(areaName),
      ...entries.slice(safeIdx),
    ];
    onChange(next);
  };
  const addRowToArea = (areaName) => {
    const next = [...entries, blankRow(areaName)];
    onChange(next);
    setExpandedArea(areaName);
  };
  const addArea = () => {
    const existing = new Set(groups.map((g) => g.name));
    let n = 1;
    let name = `Area ${n}`;
    while (existing.has(name)) { n += 1; name = `Area ${n}`; }
    addRowToArea(name);
  };
  const renameArea = (oldName, newNameRaw) => {
    const newName = (newNameRaw || "").trim();
    if (!newName || newName === oldName) { setRenamingArea(null); return; }
    onChange(entries.map((e) => (areaOf(e) === oldName ? { ...e, area: newName } : e)));
    if (onAreaNotesChange) {
      const next = { ...(areaNotes || {}) };
      if (oldName in next) {
        const prev = next[oldName]; delete next[oldName];
        if (prev) next[newName] = prev;
      }
      onAreaNotesChange(next);
    }
    if (onAreaPhotosChange) {
      const next = { ...(areaPhotos || {}) };
      if (oldName in next) {
        const prev = next[oldName]; delete next[oldName];
        if (prev && prev.length) next[newName] = prev;
      }
      onAreaPhotosChange(next);
    }
    if (onAreaMetaChange) {
      const next = { ...(areaMeta || {}) };
      if (oldName in next) {
        const prev = next[oldName]; delete next[oldName];
        if (prev && Object.keys(prev).length) next[newName] = prev;
      }
      onAreaMetaChange(next);
    }
    setExpandedArea(newName);
    setRenamingArea(null);
  };
  const removeArea = (areaName) => {
    onChange(entries.filter((e) => areaOf(e) !== areaName));
    if (onAreaNotesChange && areaNotes && areaName in areaNotes) {
      const next = { ...areaNotes }; delete next[areaName];
      onAreaNotesChange(next);
    }
    if (onAreaPhotosChange && areaPhotos && areaName in areaPhotos) {
      const next = { ...areaPhotos }; delete next[areaName];
      onAreaPhotosChange(next);
    }
    if (onAreaMetaChange && areaMeta && areaName in areaMeta) {
      const next = { ...areaMeta }; delete next[areaName];
      onAreaMetaChange(next);
    }
  };
  const updateAreaNote = (areaName, text) => {
    if (!onAreaNotesChange) return;
    const next = { ...(areaNotes || {}) };
    if (text && text.trim()) next[areaName] = text;
    else delete next[areaName];
    onAreaNotesChange(next);
  };
  /**
   * Toggle a single option in a given area-meta group for the specified area.
   * When the resulting selection becomes empty, we drop the group key so the
   * stored object stays clean (no `{type: []}` entries). Likewise when the
   * whole area becomes empty we drop it entirely — so a serialised revision
   * only carries meta for areas that actually have some selection.
   */
  const toggleAreaMeta = (areaName, groupKey, optionKey) => {
    if (!onAreaMetaChange) return;
    const next = { ...(areaMeta || {}) };
    const areaEntry = { ...(next[areaName] || {}) };
    const current = new Set(areaEntry[groupKey] || []);
    if (current.has(optionKey)) current.delete(optionKey);
    else current.add(optionKey);
    if (current.size === 0) delete areaEntry[groupKey];
    else areaEntry[groupKey] = Array.from(current);
    if (Object.keys(areaEntry).length === 0) delete next[areaName];
    else next[areaName] = areaEntry;
    onAreaMetaChange(next);
  };
  const updateAreaPhotos = (areaName, paths) => {
    if (!onAreaPhotosChange) return;
    const next = { ...(areaPhotos || {}) };
    if (paths && paths.length) next[areaName] = paths;
    else delete next[areaName];
    onAreaPhotosChange(next);
  };

  // ---- drag and drop -----------------------------------------------------
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const findIndexById = (id) => entries.findIndex((e) => e._dndId === id);

  const onDragStart = (event) => setActiveDragId(event.active.id);
  const onDragCancel = () => setActiveDragId(null);
  const onDragEnd = (event) => {
    setActiveDragId(null);
    const { active, over } = event;
    if (!over) return;
    const activeId = String(active.id);
    const overId = String(over.id);

    // -------- Area-to-area drag --------
    if (activeId.startsWith(AREA_DRAG_PREFIX)) {
      const activeName = activeId.slice(AREA_DRAG_PREFIX.length);
      let targetName = null;
      if (overId.startsWith(AREA_DRAG_PREFIX)) {
        targetName = overId.slice(AREA_DRAG_PREFIX.length);
      } else if (overId.startsWith(AREA_PLACEHOLDER_PREFIX)) {
        targetName = overId.slice(AREA_PLACEHOLDER_PREFIX.length);
      } else {
        const overEntry = entries.find((e) => e._dndId === overId);
        if (overEntry) targetName = areaOf(overEntry);
      }
      if (!targetName || targetName === activeName) return;
      // Reorder entries: move all entries of activeName to position of targetName
      const groupsArr = groupByArea(entries);
      const order = groupsArr.map((g) => g.name);
      const fromIdx = order.indexOf(activeName);
      const toIdx = order.indexOf(targetName);
      if (fromIdx === -1 || toIdx === -1) return;
      const newOrder = arrayMove(order, fromIdx, toIdx);
      const byArea = new Map();
      groupsArr.forEach((g) => byArea.set(g.name, g.items.map((it) => it.entry)));
      const newEntries = newOrder.flatMap((name) => byArea.get(name) || []);
      onChange(newEntries);
      return;
    }

    // -------- Row drag --------
    const activeIdx = findIndexById(activeId);
    if (activeIdx === -1) return;

    if (overId.startsWith(AREA_PLACEHOLDER_PREFIX) || overId.startsWith(AREA_DRAG_PREFIX)) {
      const targetArea = overId.startsWith(AREA_DRAG_PREFIX)
        ? overId.slice(AREA_DRAG_PREFIX.length)
        : overId.slice(AREA_PLACEHOLDER_PREFIX.length);
      // No-op if dropping back into same area without changing order
      if (areaOf(entries[activeIdx]) === targetArea) {
        // still consider as cross-area to-end move? Skip if same.
        return;
      }
      const next = entries.filter((_, i) => i !== activeIdx);
      const moved = { ...entries[activeIdx], area: targetArea };
      let insertAt = next.length;
      for (let i = next.length - 1; i >= 0; i--) {
        if (areaOf(next[i]) === targetArea) { insertAt = i + 1; break; }
      }
      next.splice(insertAt, 0, moved);
      onChange(next);
      // Keep the user's current expanded area: don't jump to the destination
      return;
    }

    const overIdx = findIndexById(overId);
    if (overIdx === -1 || overIdx === activeIdx) return;
    const targetArea = areaOf(entries[overIdx]);
    const renamed = entries.map((e, i) => (i === activeIdx ? { ...e, area: targetArea } : e));
    const reordered = arrayMove(renamed, activeIdx, overIdx);
    onChange(reordered);
    // Cross-area drop: keep the source area expanded (don't auto-jump).
  };

  if (groups.length === 0) {
    return (
      <div data-testid="damage-table">
        <Header groupsLen={0} entriesLen={0} readOnly={readOnly} onAddArea={addArea} />
        <div className="border-[1.5px] border-dashed border-neutral-300 rounded-2xl px-4 py-6 text-center text-sm text-neutral-500" data-testid="damage-empty">
          Nessuna area inserita. Clicca &quot;Aggiungi area&quot; per cominciare.
        </div>
      </div>
    );
  }

  const activeEntry = activeDragId ? entries[findIndexById(activeDragId)] : null;

  return (
    <div data-testid="damage-table">
      <Header groupsLen={groups.length} entriesLen={entries.length} readOnly={readOnly} onAddArea={addArea} />

      <DndContext
        sensors={sensors}
        collisionDetection={pointerWithin}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onDragCancel={onDragCancel}
      >
        <SortableContext
          items={entries.map((e) => e._dndId).filter(Boolean)}
          strategy={verticalListSortingStrategy}
        >
          <SortableContext
            items={groups.map((g) => `${AREA_DRAG_PREFIX}${g.name}`)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-3">
              {groups.map((group) => (
                <AreaCard
                  key={group.name}
                  group={group}
                  isOpen={expandedArea === group.name}
                  isRenaming={renamingArea === group.name}
                  note={(areaNotes || {})[group.name] || ""}
                  photos={(areaPhotos || {})[group.name] || []}
                  meta={(areaMeta || {})[group.name] || {}}
                  toggleAreaMeta={toggleAreaMeta}
                  readOnly={readOnly}
                  renameValue={renameValue}
                  setRenameValue={setRenameValue}
                  setRenamingArea={setRenamingArea}
                  renameArea={renameArea}
                  removeArea={removeArea}
                  setExpandedArea={setExpandedArea}
                  updateRow={updateRow}
                  removeRow={removeRow}
                  insertRow={insertRow}
                  addRowToArea={addRowToArea}
                  addArea={addArea}
                  updateAreaNote={updateAreaNote}
                  updateAreaPhotos={updateAreaPhotos}
                  activeDragId={activeDragId}
                />
              ))}
            </div>
          </SortableContext>
        </SortableContext>

        <DragOverlay dropAnimation={null}>
          {activeDragId && String(activeDragId).startsWith(AREA_DRAG_PREFIX) ? (
            <div className="border-[1.5px] border-black bg-[#0A0A0A] text-white px-3 py-2.5 rounded-2xl shadow-2xl opacity-95 inline-flex items-center gap-2">
              <GripVertical className="w-5 h-5 text-[#ff5a00]" />
              <span className="font-bold text-sm tracking-wide">
                {String(activeDragId).slice(AREA_DRAG_PREFIX.length)}
              </span>
            </div>
          ) : activeEntry ? (
            <div className="bru-card p-3 shadow-2xl border-2 border-[#ff5a00] opacity-95 max-w-[640px]">
              <div className="flex items-center gap-2">
                <GripVertical className="w-5 h-5 text-[#ff5a00]" />
                <div className="flex-1 truncate text-sm">
                  <span className="font-bold">{activeEntry.riferimento_scaffalatura || "—"}</span>
                  <span className="text-neutral-500"> · {activeEntry.posto || "—"}</span>
                  <span className="text-neutral-500"> · area: {areaOf(activeEntry)}</span>
                </div>
              </div>
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
    </div>
  );
}

function Header({ groupsLen, entriesLen, readOnly, onAddArea }) {
  return (
    <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
      <div className="tag-label">
        Dettaglio Controllo · {groupsLen} area{groupsLen === 1 ? "" : "e"} · {entriesLen} rig{entriesLen === 1 ? "a" : "he"}
      </div>
      {!readOnly && (
        <button type="button" onClick={onAddArea} className="bru-btn bru-btn-secondary !px-3 !py-1.5 text-xs" data-testid="damage-add-area">
          <FolderPlus className="w-4 h-4" />
          Aggiungi area
        </button>
      )}
    </div>
  );
}

function AreaCard({
  group, isOpen, isRenaming, note, photos, meta, toggleAreaMeta, readOnly, renameValue, setRenameValue, setRenamingArea,
  renameArea, removeArea, setExpandedArea, updateRow, removeRow, insertRow, addRowToArea, addArea, updateAreaNote,
  updateAreaPhotos, activeDragId,
}) {
  const activeIsArea = typeof activeDragId === "string" && activeDragId.startsWith(AREA_DRAG_PREFIX);
  const { setNodeRef: setHeaderDropRef, isOver: isHeaderOver } = useDroppable({
    id: `${AREA_PLACEHOLDER_PREFIX}${group.name}`,
    disabled: readOnly || activeIsArea,
  });
  const {
    setNodeRef: setAreaSortRef,
    attributes: areaAttrs,
    listeners: areaListeners,
    transform: areaTransform,
    transition: areaTransition,
    isDragging: areaIsDragging,
  } = useSortable({ id: `${AREA_DRAG_PREFIX}${group.name}`, disabled: readOnly });

  const areaStyle = {
    transform: CSS.Transform.toString(areaTransform),
    transition: areaTransition,
    opacity: areaIsDragging ? 0.35 : 1,
  };

  return (
    <div
      ref={setAreaSortRef}
      style={areaStyle}
      className="border-[1.5px] border-black rounded-2xl bg-white overflow-hidden"
      data-testid={`damage-area-${group.name}`}
    >
      {/* header */}
      <div
        ref={!isOpen ? setHeaderDropRef : undefined}
        className={`flex items-center gap-2 px-3 py-2.5 transition-colors ${
          isOpen
            ? "bg-[#0A0A0A] text-white"
            : isHeaderOver && !activeIsArea
            ? "bg-[#FFE9DA] text-neutral-900 ring-2 ring-[#ff5a00]"
            : "bg-neutral-100 text-neutral-900 cursor-pointer hover:bg-neutral-200"
        }`}
        onClick={(ev) => {
          if (readOnly && !isOpen) { ev.preventDefault(); setExpandedArea(group.name); }
        }}
      >
        {!readOnly && (
          <button
            type="button"
            {...areaAttrs}
            {...areaListeners}
            onClick={(ev) => ev.stopPropagation()}
            className={`shrink-0 cursor-grab active:cursor-grabbing touch-none p-0.5 ${isOpen ? "text-white/60 hover:text-white" : "text-neutral-400 hover:text-black"}`}
            title="Trascina per riordinare l'area"
            aria-label="Trascina l'area"
            data-testid={`area-drag-${group.name}`}
          >
            <GripVertical className="w-4 h-4" />
          </button>
        )}
        <button
          type="button"
          onClick={(ev) => { ev.stopPropagation(); setExpandedArea(isOpen ? null : group.name); }}
          className="shrink-0"
          aria-label={isOpen ? "Chiudi area" : "Espandi area"}
          data-testid={`area-toggle-${group.name}`}
        >
          {isOpen ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
        </button>
        <div className="flex-1 min-w-0">
          {isRenaming && !readOnly ? (
            <input
              autoFocus
              type="text"
              defaultValue={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onBlur={(e) => renameArea(group.name, e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") { e.preventDefault(); renameArea(group.name, e.currentTarget.value); }
                else if (e.key === "Escape") { setRenamingArea(null); }
              }}
              onClick={(ev) => ev.stopPropagation()}
              className="w-full bg-white text-black px-2 py-1 rounded text-sm font-bold"
              data-testid={`area-rename-input-${group.name}`}
            />
          ) : (
            <button
              type="button"
              onClick={(ev) => {
                ev.stopPropagation();
                if (readOnly) { setExpandedArea(isOpen ? null : group.name); return; }
                setRenamingArea(group.name);
                setRenameValue(group.name);
              }}
              className="font-bold text-sm tracking-wide truncate text-left flex items-center gap-1.5 hover:opacity-80 w-full"
              title={readOnly ? "Espandi/comprimi" : "Rinomina area"}
              data-testid={`area-name-${group.name}`}
            >
              <span className="truncate">{group.name}</span>
              {!readOnly && <Pencil className="w-3 h-3 opacity-60" />}
            </button>
          )}
        </div>
        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-bold ${isOpen ? "bg-white/15" : "bg-white border border-black"}`}>
          {group.items.length} rig{group.items.length === 1 ? "a" : "he"}
        </span>
        {(photos || []).length > 0 && (
          <span
            className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-bold inline-flex items-center gap-1 ${isOpen ? "bg-[#ff5a00] text-white" : "bg-[#ff5a00] text-white"}`}
            title={`${photos.length} foto allegate all'area`}
            data-testid={`area-photo-count-${group.name}`}
          >
            <Camera className="w-3 h-3" />
            {photos.length}
          </span>
        )}
        {!readOnly && (
          <button
            type="button"
            onClick={(ev) => { ev.stopPropagation(); removeArea(group.name); }}
            className={`shrink-0 ml-1 ${isOpen ? "text-white/70 hover:text-[#ff3b30]" : "text-neutral-500 hover:text-[#ff3b30]"}`}
            title="Elimina area (e tutte le righe)"
            data-testid={`area-remove-${group.name}`}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* body */}
      {isOpen && (
        <div className="p-3 sm:p-4">
          <AreaMetaSection
            areaName={group.name}
            meta={meta}
            toggleAreaMeta={toggleAreaMeta}
            readOnly={readOnly}
          />
          {readOnly ? (
            note ? (
              <div className="mb-3 rounded-lg border border-[#ff5a00]/30 bg-[#fff7f0] px-3 py-2" data-testid={`area-note-${group.name}`}>
                <div className="tag-label !text-[10px] mb-0.5">Note area</div>
                <div className="text-sm whitespace-pre-line">{note}</div>
              </div>
            ) : null
          ) : (
            <div className="mb-3">
              <label className="bru-label !mb-1">Note generali area</label>
              <textarea
                rows={2}
                value={note}
                onChange={(e) => updateAreaNote(group.name, e.target.value)}
                className="bru-input !py-2 !px-3 !text-sm"
                placeholder={`Note generali per "${group.name}"…`}
                data-testid={`area-note-input-${group.name}`}
              />
            </div>
          )}

          {!readOnly && (
            <div className="mb-3">
              <label className="bru-label !mb-1 flex items-center gap-1.5">
                <Camera className="w-3.5 h-3.5" />
                Foto area · opzionali
              </label>
              <PhotoUploader
                value={photos || []}
                onChange={(arr) => updateAreaPhotos(group.name, arr)}
                max={5}
              />
            </div>
          )}
          {readOnly && (photos || []).length > 0 && (
            <div className="mb-3">
              <label className="bru-label !mb-1 flex items-center gap-1.5">
                <Camera className="w-3.5 h-3.5" />
                Foto area
              </label>
              <PhotoUploader
                value={photos || []}
                onChange={() => {}}
                max={5}
                readOnly
              />
            </div>
          )}

          <AreaBody group={group} readOnly={readOnly} updateRow={updateRow} removeRow={removeRow} insertRow={insertRow} />

          {!readOnly && (
            <>
              <button
                type="button"
                onClick={() => addRowToArea(group.name)}
                className="mt-3 w-full bru-btn bru-btn-secondary !py-2 border-2 border-dashed !border-neutral-300 text-xs"
                data-testid={`area-add-row-${group.name}`}
              >
                <Plus className="w-4 h-4" />
                Aggiungi riga a &quot;{group.name}&quot;
              </button>
              <button
                type="button"
                onClick={addArea}
                className="mt-2 w-full bru-btn bru-btn-secondary !py-2 text-xs"
                data-testid={`area-add-area-after-${group.name}`}
              >
                <FolderPlus className="w-4 h-4" />
                Aggiungi nuova area
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// Total <td> count for the desktop damage table (used by InsertGapRow's
// colSpan). Three fixed cols (#, Riferimento, Posto) + 5 damage cols + 3
// trailing (Nota#1, Nota#2, Foto). When editing, +1 leading (drag handle)
// and +1 trailing (delete). Keeping this in one place avoids accidental
// drift when columns change.
function damageTableColSpan(readOnly) {
  return (readOnly ? 0 : 2) + 3 + DAMAGE_COLS.length + 3;
}

function InsertGapRow({ readOnly, colSpan, onInsert, testid }) {
  if (readOnly) return null;
  return (
    <tr className="group h-2 hover:h-7 transition-all" data-testid={testid}>
      <td colSpan={colSpan} className="p-0">
        <button
          type="button"
          onClick={onInsert}
          className="w-full h-2 group-hover:h-7 flex items-center justify-center text-[11px] font-bold uppercase tracking-wider text-transparent group-hover:text-[#ff5a00] transition-all border-y border-dashed border-transparent group-hover:border-[#ff5a00] bg-transparent group-hover:bg-[#fff7f0]"
          title="Inserisci nuova riga qui"
          data-testid={`${testid}-btn`}
        >
          + Inserisci riga qui
        </button>
      </td>
    </tr>
  );
}

function InsertGapDiv({ readOnly, onInsert, testid }) {
  if (readOnly) return null;
  return (
    <div className="group" data-testid={testid}>
      <button
        type="button"
        onClick={onInsert}
        className="w-full h-3 group-hover:h-8 flex items-center justify-center text-[11px] font-bold uppercase tracking-wider text-transparent group-hover:text-[#ff5a00] transition-all border-y border-dashed border-transparent group-hover:border-[#ff5a00] bg-transparent group-hover:bg-[#fff7f0] rounded"
        title="Inserisci nuova riga qui"
        data-testid={`${testid}-btn`}
      >
        + Inserisci riga qui
      </button>
    </div>
  );
}

function AreaMetaSection({ areaName, meta = {}, toggleAreaMeta, readOnly }) {
  // In read-only mode we only render the selections that ARE set — if the
  // whole meta object is empty for this area, render nothing at all. This
  // mirrors the PDF: no selection ⇒ no visible section.
  if (readOnly) {
    const hasAny = AREA_META_GROUPS.some((g) => (meta?.[g.key] || []).length > 0);
    if (!hasAny) return null;
    return (
      <div className="mb-3 border-[1.5px] border-black bg-white px-3 py-2 space-y-1" data-testid={`area-meta-view-${areaName}`}>
        {AREA_META_GROUPS.map((g) => {
          const sel = meta?.[g.key] || [];
          if (!sel.length) return null;
          const labels = sel.map((k) => AREA_META_LOOKUP[g.key].options[k] || k);
          return (
            <div key={g.key} className="text-sm">
              <span className="tag-label !text-[10px]">{g.label}</span>
              <span className="ml-2 font-medium">{labels.join(" · ")}</span>
            </div>
          );
        })}
      </div>
    );
  }
  return (
    <div className="mb-3 border-[1.5px] border-neutral-200 bg-white px-3 py-3 space-y-3" data-testid={`area-meta-${areaName}`}>
      {AREA_META_GROUPS.map((g) => {
        const sel = new Set(meta?.[g.key] || []);
        return (
          <div key={g.key}>
            <div className="tag-label !text-[10px] mb-1">{g.label}</div>
            <div className="flex flex-wrap gap-1.5">
              {g.options.map((o) => {
                const on = sel.has(o.key);
                return (
                  <button
                    key={o.key}
                    type="button"
                    onClick={() => toggleAreaMeta(areaName, g.key, o.key)}
                    className={`px-2.5 py-1 text-xs font-bold border-[1.5px] border-black transition-colors ${
                      on ? "bg-black text-white" : "bg-white text-black hover:bg-[#fff7f0]"
                    }`}
                    aria-pressed={on}
                    data-testid={`area-meta-${areaName}-${g.key}-${o.key}`}
                  >
                    {o.label}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}


function AreaBody({ group, readOnly, updateRow, removeRow, insertRow }) {
  const isDesktop = useIsDesktop();
  // Render ONE variant only — keeps each row's `useSortable` registered once
  if (isDesktop) {
    return (
      <DesktopTable group={group} readOnly={readOnly} updateRow={updateRow} removeRow={removeRow} insertRow={insertRow} />
    );
  }
  return (
    <MobileCards group={group} readOnly={readOnly} updateRow={updateRow} removeRow={removeRow} insertRow={insertRow} />
  );
}

// ============================================================================
// Desktop: original compact <table> layout with leading drag-handle column
// ============================================================================
function DesktopTable({ group, readOnly, updateRow, removeRow, insertRow }) {
  return (
    <div className="border-[1.5px] border-black rounded-xl overflow-hidden bg-white" data-testid={`area-rows-${group.name}`}>
      <table className="w-full text-[11px] border-collapse table-fixed">
        <colgroup>
          {!readOnly && <col style={{ width: "28px" }} />}
          <col style={{ width: "30px" }} />
          <col style={{ width: "12%" }} />
          <col style={{ width: "8%" }} />
          <col style={{ width: "7.5%" }} />
          <col style={{ width: "7.5%" }} />
          <col style={{ width: "7.5%" }} />
          <col style={{ width: "7.5%" }} />
          <col style={{ width: "7.5%" }} />
          <col style={{ width: "13%" }} />
          <col style={{ width: "13%" }} />
          <col style={{ width: "56px" }} />
          {!readOnly && <col style={{ width: "32px" }} />}
        </colgroup>
        <thead className="bg-black text-white">
          <tr>
            {!readOnly && <th className="p-1.5 border-r border-neutral-700"></th>}
            <th className="p-1.5 border-r border-neutral-700">#</th>
            <th className="p-1.5 border-r border-neutral-700 text-left">Riferimento</th>
            <th className="p-1.5 border-r border-neutral-700 text-left">Posto</th>
            {DAMAGE_COLS.map((c) => (
              <th key={c.key} className="p-1.5 border-r border-neutral-700" title={c.long}>{c.short}</th>
            ))}
            <th className="p-1.5 border-r border-neutral-700 text-left">Nota #1</th>
            <th className="p-1.5 border-r border-neutral-700 text-left">Nota #2</th>
            <th className="p-1.5 border-r border-neutral-700">Foto</th>
            {!readOnly && <th className="p-1.5"></th>}
          </tr>
        </thead>
        <tbody>
          {group.items.map(({ entry, idx }) => (
            <React.Fragment key={entry._dndId || `pos-${idx}`}>
              <InsertGapRow
                readOnly={readOnly}
                colSpan={damageTableColSpan(readOnly)}
                onInsert={() => insertRow(idx, group.name)}
                testid={`insert-row-before-${idx}`}
              />
              <SortableTableRow
                entry={entry}
                idx={idx}
                readOnly={readOnly}
                updateRow={updateRow}
                removeRow={removeRow}
              />
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SortableTableRow({ entry, idx, readOnly, updateRow, removeRow }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: entry._dndId,
    disabled: readOnly,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.35 : 1,
    background: isDragging ? "#FFF7F0" : undefined,
  };
  return (
    <tr
      ref={setNodeRef}
      style={style}
      className="border-t border-neutral-200 align-top"
      data-testid={`damage-row-${idx}`}
    >
      {!readOnly && (
        <td className="p-0 border-r border-neutral-200 align-middle text-center">
          <button
            type="button"
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing touch-none text-neutral-400 hover:text-black p-1 w-full h-full flex items-center justify-center"
            title="Trascina per riordinare"
            aria-label="Trascina la riga"
            data-testid={`damage-drag-${idx}`}
          >
            <GripVertical className="w-4 h-4" />
          </button>
        </td>
      )}
      <td className="p-1 border-r border-neutral-200 font-bold text-center font-mono">{idx + 1}</td>
      <td className="p-1 border-r border-neutral-200">
        {readOnly ? (
          <div className="text-[11px] py-1 px-1">{entry.riferimento_scaffalatura || "—"}</div>
        ) : (
          <input
            className="w-full border-[1.5px] border-neutral-300 rounded px-1.5 py-1 text-[11px] focus:border-black focus:outline-none"
            value={entry.riferimento_scaffalatura || ""}
            onChange={(ev) => updateRow(idx, { riferimento_scaffalatura: ev.target.value })}
            placeholder="SCF-A"
            data-testid={`damage-rif-${idx}`}
          />
        )}
      </td>
      <td className="p-1 border-r border-neutral-200">
        {readOnly ? (
          <div className="text-[11px] py-1 px-1">{entry.posto || "—"}</div>
        ) : (
          <input
            className="w-full border-[1.5px] border-neutral-300 rounded px-1.5 py-1 text-[11px] focus:border-black focus:outline-none"
            value={entry.posto || ""}
            onChange={(ev) => updateRow(idx, { posto: ev.target.value })}
            placeholder="A1"
            data-testid={`damage-posto-${idx}`}
          />
        )}
      </td>
      {DAMAGE_COLS.map((c) => (
        <td key={c.key} className="p-1 border-r border-neutral-200">
          {readOnly ? (
            <div className="flex justify-center">
              {entry[c.key] ? (
                <span
                  className={`inline-flex items-center justify-center w-5 h-5 border border-black leading-none font-bold ${
                    entry[c.key] === "verde" ? "bg-[#10b981] text-white" :
                    entry[c.key] === "arancione" ? "bg-[#ffcc00] text-black" : "bg-[#ff3b30] text-white"
                  }`}
                  data-testid={`damage-${c.key}-${idx}-readonly`}
                >
                  {(() => {
                    if (!c.countable) return "";
                    const ns = normaliseCounts(entry[countKey(c.key)]);
                    if (!ns) return "";
                    const shown = ns.slice(0, 4).join(",");
                    const txt = ns.length > 4 ? `${shown}…` : shown;
                    const px = txt.length <= 1 ? 10 : txt.length <= 3 ? 9 : 7;
                    return <span style={{ fontSize: `${px}px` }} className="whitespace-nowrap">{txt}</span>;
                  })()}
                </span>
              ) : (
                <span className="text-neutral-400">—</span>
              )}
            </div>
          ) : (
            <LevelPicker
              value={entry[c.key]}
              onChange={(v) => updateRow(idx, c.countable ? { [c.key]: v, [countKey(c.key)]: v ? entry[countKey(c.key)] : null } : { [c.key]: v })}
              countable={!!c.countable}
              count={c.countable ? entry[countKey(c.key)] : null}
              onCountChange={c.countable ? (n) => updateRow(idx, { [countKey(c.key)]: n }) : null}
            />
          )}
        </td>
      ))}
      <td className="p-1 border-r border-neutral-200">
        {readOnly ? (
          <div className="text-[11px] py-1 px-1 leading-tight">{NOTE1_OPTIONS.find((o) => o.value === entry.nota1)?.label || "—"}</div>
        ) : (
          <select
            className="w-full border-[1.5px] border-neutral-300 rounded px-1 py-1 text-[11px] focus:border-black focus:outline-none"
            value={entry.nota1 || ""}
            onChange={(ev) => updateRow(idx, { nota1: ev.target.value || null })}
            data-testid={`damage-nota1-${idx}`}
          >
            {NOTE1_OPTIONS.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
          </select>
        )}
      </td>
      <td className="p-1 border-r border-neutral-200">
        {readOnly ? (
          <div className="text-[11px] py-1 px-1 whitespace-pre-line leading-tight">{entry.nota2 || "—"}</div>
        ) : (
          <input
            className="w-full border-[1.5px] border-neutral-300 rounded px-1.5 py-1 text-[11px] focus:border-black focus:outline-none"
            value={entry.nota2 || ""}
            onChange={(ev) => updateRow(idx, { nota2: ev.target.value })}
            placeholder="Testo libero"
            data-testid={`damage-nota2-${idx}`}
          />
        )}
      </td>
      <td className="p-1 border-r border-neutral-200">
        <div className="flex justify-center">
          <SinglePhotoCell value={entry.photo} onChange={(p) => updateRow(idx, { photo: p })} size="sm" readOnly={readOnly} />
        </div>
      </td>
      {!readOnly && (
        <td className="p-1 text-center align-middle">
          <button
            type="button"
            onClick={() => removeRow(idx)}
            className="text-neutral-500 hover:text-[#ff3b30]"
            title="Rimuovi riga"
            data-testid={`damage-remove-${idx}`}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </td>
      )}
    </tr>
  );
}

// ============================================================================
// Mobile: card layout (one card per row) with leading drag-handle
// ============================================================================
function MobileCards({ group, readOnly, updateRow, removeRow, insertRow }) {
  return (
    <div className="space-y-3" data-testid={`area-rows-${group.name}`}>
      {group.items.map(({ entry, idx }) => (
        <React.Fragment key={entry._dndId || `pos-${idx}`}>
          <InsertGapDiv
            readOnly={readOnly}
            onInsert={() => insertRow(idx, group.name)}
            testid={`insert-card-before-${idx}`}
          />
          <SortableMobileCard
            entry={entry}
            idx={idx}
            readOnly={readOnly}
            updateRow={updateRow}
            removeRow={removeRow}
          />
        </React.Fragment>
      ))}
    </div>
  );
}

function SortableMobileCard({ entry, idx, readOnly, updateRow, removeRow }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: entry._dndId,
    disabled: readOnly,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.35 : 1,
  };
  return (
    <div ref={setNodeRef} style={style} className="bru-card p-4" data-testid={`damage-row-${idx}`}>
      <div className="flex items-center justify-between mb-3 pb-3 border-b border-neutral-100">
        {!readOnly && (
          <button
            type="button"
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing touch-none text-neutral-400 hover:text-black p-1 mr-2"
            title="Trascina per riordinare"
            aria-label="Trascina la riga"
            data-testid={`damage-drag-${idx}`}
          >
            <GripVertical className="w-5 h-5" />
          </button>
        )}
        <div className="flex-1 font-bold text-base">Riga #{idx + 1}</div>
        {!readOnly && (
          <button
            type="button"
            onClick={() => removeRow(idx)}
            className="text-neutral-500 hover:text-[#ff3b30]"
            title="Rimuovi riga"
            data-testid={`damage-remove-${idx}`}
          >
            <Trash2 className="w-5 h-5" />
          </button>
        )}
      </div>
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <label className="bru-label !mb-1">Riferimento</label>
          {readOnly ? (
            <div className="text-sm font-medium">{entry.riferimento_scaffalatura || "—"}</div>
          ) : (
            <input
              className="bru-input !py-2 !px-3 !text-sm"
              value={entry.riferimento_scaffalatura || ""}
              onChange={(ev) => updateRow(idx, { riferimento_scaffalatura: ev.target.value })}
              placeholder="Es. SCF-A"
              data-testid={`damage-rif-${idx}`}
            />
          )}
        </div>
        <div>
          <label className="bru-label !mb-1">Posto</label>
          {readOnly ? (
            <div className="text-sm font-medium">{entry.posto || "—"}</div>
          ) : (
            <input
              className="bru-input !py-2 !px-3 !text-sm"
              value={entry.posto || ""}
              onChange={(ev) => updateRow(idx, { posto: ev.target.value })}
              placeholder="Es. A1"
              data-testid={`damage-posto-${idx}`}
            />
          )}
        </div>
      </div>
      <div className="space-y-2 mb-3">
        <label className="bru-label !mb-1">Componenti</label>
        <div className="grid grid-cols-1 gap-1.5">
          {DAMAGE_COLS.map((c) => (
            <div key={c.key} className="flex items-center justify-between gap-3 bg-neutral-50 rounded-lg px-3 py-2">
              <div className="text-xs font-semibold text-neutral-700 flex-1">{c.long}</div>
              {readOnly ? (
                entry[c.key] ? (
                  <span
                    className={`inline-flex items-center justify-center w-6 h-6 rounded leading-none font-bold ${
                      entry[c.key] === "verde" ? "bg-[#10b981] text-white" :
                      entry[c.key] === "arancione" ? "bg-[#ffcc00] text-black" : "bg-[#ff3b30] text-white"
                    }`}
                  >
                    {(() => {
                      if (!c.countable) return "";
                      const ns = normaliseCounts(entry[countKey(c.key)]);
                      if (!ns) return "";
                      const shown = ns.slice(0, 4).join(",");
                      const txt = ns.length > 4 ? `${shown}…` : shown;
                      const px = txt.length <= 1 ? 11 : txt.length <= 3 ? 9 : 7;
                      return <span style={{ fontSize: `${px}px` }} className="whitespace-nowrap">{txt}</span>;
                    })()}
                  </span>
                ) : (<span className="text-neutral-400 text-xs">—</span>)
              ) : (
                <LevelPicker
                  value={entry[c.key]}
                  onChange={(v) => updateRow(idx, c.countable ? { [c.key]: v, [countKey(c.key)]: v ? entry[countKey(c.key)] : null } : { [c.key]: v })}
                  countable={!!c.countable}
                  count={c.countable ? entry[countKey(c.key)] : null}
                  onCountChange={c.countable ? (n) => updateRow(idx, { [countKey(c.key)]: n }) : null}
                />
              )}
            </div>
          ))}
        </div>
      </div>
      <div className="space-y-2 mb-3">
        <div>
          <label className="bru-label !mb-1">Nota #1</label>
          {readOnly ? (
            <div className="text-sm">{NOTE1_OPTIONS.find((o) => o.value === entry.nota1)?.label || "—"}</div>
          ) : (
            <select
              className="bru-input !py-2 !px-3 !text-sm"
              value={entry.nota1 || ""}
              onChange={(ev) => updateRow(idx, { nota1: ev.target.value || null })}
              data-testid={`damage-nota1-${idx}`}
            >
              {NOTE1_OPTIONS.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
            </select>
          )}
        </div>
        <div>
          <label className="bru-label !mb-1">Nota #2</label>
          {readOnly ? (
            <div className="text-sm whitespace-pre-line">{entry.nota2 || "—"}</div>
          ) : (
            <input
              className="bru-input !py-2 !px-3 !text-sm"
              value={entry.nota2 || ""}
              onChange={(ev) => updateRow(idx, { nota2: ev.target.value })}
              placeholder="Testo libero"
              data-testid={`damage-nota2-${idx}`}
            />
          )}
        </div>
      </div>
      <div>
        <label className="bru-label !mb-1">Foto Danno</label>
        <SinglePhotoCell value={entry.photo} onChange={(p) => updateRow(idx, { photo: p })} size="sm" readOnly={readOnly} />
      </div>
    </div>
  );
}
